import { useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { notifyQuotaExhausted } from "../lib/quota";
import { MarkdownText } from "./MarkdownText";
import { CopyReadingButton } from "./CopyReadingButton";
import { DeleteReadingButton } from "./DeleteReadingButton";
import { MessageCircleQuestion, Send, Loader2 } from "lucide-react";

export const ContinueReading = ({ sessionId, renderPicker, testid }) => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("form");
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);

  const start = async () => {
    const q = question.trim();
    if (q.length < 3 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/draw-sessions/${currentSessionId}/followup/start`, { question: q });
      setSession(data);
      setCurrentSessionId(data.id);
      setThread((t) => [...t, { q, cards: [], a: "", streaming: true }]);
      setQuestion("");
      setPhase("pick");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") {
        notifyQuotaExhausted();
      } else {
        toast.error(typeof d === "string" ? d : "Qualcosa è andato storto. Riprova.");
      }
    } finally {
      setBusy(false);
    }
  };

  const interpret = async (sess, q) => {
    setPhase("reading");
    try {
      const res = await fetch(`${API}/draw-sessions/${sess.id}/followup`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Qualcosa è andato storto. Riprova.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "meta") {
            setThread((t) => t.map((m, i) => (i === t.length - 1 ? { ...m, cards: ev.cards || [] } : m)));
          } else if (ev.type === "delta") {
            setThread((t) => t.map((m, i) => (i === t.length - 1 ? { ...m, a: ev.content, streaming: false } : m)));
          } else if (ev.type === "error") {
            throw new Error(ev.content);
          }
        }
      }
    } catch (e) {
      setThread((t) => t.slice(0, -1));
      setQuestion(q);
      toast.error(e.message || "Qualcosa è andato storto. Riprova.");
    } finally {
      setPhase("form");
      setSession(null);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`${testid}-continue-btn`}
        className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-full border border-purple-400/50 text-purple-200 hover:border-amber-500/50 hover:text-amber-200 transition-colors"
      >
        <MessageCircleQuestion className="w-3.5 h-3.5" /> Continua questa lettura
      </button>
    );
  }

  return (
    <div className="mystic-card p-6 sm:p-7 space-y-5" data-testid={`${testid}-followup-panel`}>
      <p className="eyebrow">Continua questa lettura</p>

      {thread.map((m, i) => (
        <div key={i} className="space-y-3" data-testid={`${testid}-followup-${i}`}>
          <p className="text-sm text-purple-300/70 italic">“{m.q}”</p>
          {m.cards.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid={`${testid}-followup-cards-${i}`}>
              {m.cards.map((c, j) => (
                <span
                  key={j}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${
                    c.reversed ? "border-fuchsia-500/40 text-fuchsia-300" : "border-amber-500/40 text-amber-300"
                  }`}
                >
                  {c.name} · {c.reversed ? "Rovesciata" : "Dritta"} · {c.position}
                </span>
              ))}
            </div>
          )}
          <div className="text-purple-100/85 leading-loose text-sm whitespace-pre-line rounded-xl bg-[#0f0617] border border-[#4a2175] p-4">
            <MarkdownText text={m.a} />
            {m.streaming && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
          </div>
          {m.a && !m.streaming && (
            <div className="flex justify-center">
            </div>
          )}
        </div>
      ))}

      {thread.some((m) => m.a && !m.streaming) && (
        <div className="flex justify-center gap-3 flex-wrap">
          <CopyReadingButton
            testid={`${testid}-copy-followups`}
            text={thread.filter((m) => m.a).map((m) => `Domanda: ${m.q}\n\n${m.a}`).join("\n\n———\n\n")}
          />
          <DeleteReadingButton
            testid={`${testid}-delete-followups`}
            onDelete={async () => {
              await api.delete(`/draw-sessions/${sessionId}/followups`);
              setThread([]);
              toast.success("Testo eliminato.");
            }}
          />
        </div>
      )}

      {phase === "pick" && session && (
        <div data-testid={`${testid}-followup-pick`}>
          <p className="eyebrow text-center mb-2">Nuova estrazione per la tua domanda</p>
          <p className="text-xs text-purple-300/60 text-center mb-6 max-w-lg mx-auto leading-relaxed">
            Il mazzo è stato mescolato di nuovo. Tocca le {session.count} carte che senti tue.
          </p>
          {renderPicker(session, () => interpret(session, thread[thread.length - 1]?.q || ""))}
        </div>
      )}

      {phase === "reading" && (
        <p className="text-purple-300/50 text-sm text-center" data-testid={`${testid}-followup-reading`}>
          Arcana sta interpretando le nuove carte…
        </p>
      )}

      {phase === "form" && (
        <>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={2}
            maxLength={600}
            placeholder="Fai un'altra domanda su questa stessa consultazione…"
            data-testid={`${testid}-followup-input`}
            className="input-dark"
            disabled={busy}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-purple-300/50">Ogni nuova domanda prevede una nuova pesca e conta come una consultazione del tuo piano.</p>
            <button
              onClick={start}
              disabled={busy || question.trim().length < 3}
              data-testid={`${testid}-followup-send`}
              className="gold-btn px-6 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {busy ? "Arcana sta mescolando…" : "Mescola e scegli le carte"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
